import { useEffect, useMemo, useRef, useState } from 'react'
import { useMe } from '../hooks/useMe'
import {
  listModqueueEvents,
  getModqueueEvent,
  claimModqueueEvent,
  releaseModqueueEvent,
  resolveModqueueEvent,
  getRelatedActor,
  getRelatedDomains,
  getRelatedRevisions,
  getRelatedSimhash,
  bulkResolveEvents,
  reviewAppeal,
  type ModQueueEvent,
  type ModQueueEventDetail,
  type ModQueueResolvePayload,
  type ModQueueUser,
  type ModQueueBulkResolvePayload,
} from '../api/moderation'
import { mapActionKeyToLabel } from '../lib/moderationLabels'

const QUEUE_OPTIONS = [
  { value: 'human_review', label: 'Human Review' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'auto', label: 'Auto' },
]

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'closed', label: 'Closed' },
]

const CONTENT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'post', label: 'Post' },
  { value: 'comment', label: 'Comment' },
  { value: 'profile', label: 'Profile' },
  { value: 'handle', label: 'Handle' },
  { value: 'listing', label: 'Listing' },
  { value: 'link', label: 'Link' },
  { value: 'dm', label: 'DM' },
  { value: 'other', label: 'Other' },
]

const ACTION_OPTIONS = [
  'allow',
  'soft_block',
  'remove',
  'require_edit',
  'derank',
  'label',
  'suspend',
  'ban',
]

const CATEGORY_OPTIONS = [
  'spam',
  'scam',
  'impersonation',
  'harassment',
  'adult',
  'minor_safety',
  'self_harm',
  'violence',
  'hate',
  'other',
]

const RESOLUTION_OPTIONS = ['upheld', 'overridden', 'dismissed']

function formatUserLabel(user?: ModQueueUser | null) {
  if (!user) return 'System'
  return user.username || user.email || user.id || 'Unknown'
}

function formatDate(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function formatRelative(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 0) return 'just now'
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function summarizeTrustFactors(factors: Record<string, any> | null | undefined) {
  if (!factors || typeof factors !== 'object') {
    return { positive: [] as Array<{ key: string; delta: number }>, negative: [] as Array<{ key: string; delta: number }> }
  }
  const entries = Object.entries(factors)
    .filter(([key, value]) => key !== 'base' && key !== 'notes' && value && typeof value === 'object' && 'delta' in value)
    .map(([key, value]) => {
      const delta = Number((value as any).delta ?? 0)
      return Number.isNaN(delta) ? null : { key, delta }
    })
    .filter((item): item is { key: string; delta: number } => !!item && item.delta !== 0)

  const positive = entries
    .filter((item) => item.delta > 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 2)
  const negative = entries
    .filter((item) => item.delta < 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 3)

  return { positive, negative }
}

type ModerationQueueProps = {
  showPageTitle?: boolean
  containerClassName?: string
  pageClassName?: string
}

export default function ModerationQueue({
  showPageTitle = true,
  containerClassName,
  pageClassName,
}: ModerationQueueProps) {
  const { me } = useMe(true)
  const [events, setEvents] = useState<ModQueueEvent[]>([])
  const [nextUrl, setNextUrl] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selected, setSelected] = useState<ModQueueEventDetail | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [resolveLoading, setResolveLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [bulkError, setBulkError] = useState<string | null>(null)
  const [appealError, setAppealError] = useState<string | null>(null)
  const [appealNotes, setAppealNotes] = useState<Record<string, string>>({})
  const [appealUserNotes, setAppealUserNotes] = useState<Record<string, string>>({})
  const [appealLoading, setAppealLoading] = useState<Record<string, boolean>>({})

  const [queueFilter, setQueueFilter] = useState('human_review')
  const [statusFilter, setStatusFilter] = useState('pending')
  const [contentTypeFilter, setContentTypeFilter] = useState('')
  const [assignedFilter, setAssignedFilter] = useState<'all' | 'me' | 'unassigned'>('all')
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)
  const pollingPausedRef = useRef(false)

  const [relatedTab, setRelatedTab] = useState<'actor' | 'domains' | 'revisions' | 'simhash'>('actor')
  const [relatedItems, setRelatedItems] = useState<ModQueueEvent[]>([])
  const [relatedLoading, setRelatedLoading] = useState(false)
  const [relatedError, setRelatedError] = useState<string | null>(null)

  const meId = me?.id ? String(me.id) : null
  const visibleIds = useMemo(() => events.map((item) => item.event_id), [events])
  const allVisibleSelected = useMemo(
    () => visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id)),
    [visibleIds, selectedIds]
  )
  const hasSelection = selectedIds.size > 0

  const resolveDefaults = useMemo(() => {
    const decision = selected?.current_decision
    const confidence = decision?.confidence ? Number(decision.confidence) : 0.6
    return {
      final_action: decision?.action || 'allow',
      categories: decision?.categories || [],
      severity: decision?.severity ?? 50,
      confidence: Number.isNaN(confidence) ? 0.6 : confidence,
      resolution: 'upheld' as ModQueueResolvePayload['resolution'],
      rationale_short: decision?.rationale_short || '',
      rationale_internal: decision?.rationale_internal || '',
      resolution_note: '',
    }
  }, [selected])

  const [resolveForm, setResolveForm] = useState<ModQueueResolvePayload>(resolveDefaults)
  useEffect(() => {
    setResolveForm(resolveDefaults)
  }, [resolveDefaults])

  const bulkDefaults = useMemo(
    () => ({
      final_action: 'allow',
      categories: [],
      severity: 50,
      confidence: 0.6,
      resolution: 'upheld' as ModQueueResolvePayload['resolution'],
      rationale_short: '',
      rationale_internal: '',
      resolution_note: '',
    }),
    []
  )
  const [bulkForm, setBulkForm] = useState<ModQueueResolvePayload>(bulkDefaults)

  const loadEvents = async () => {
    setListLoading(true)
    setError(null)
    try {
      const params: Record<string, string> = {}
      if (queueFilter) params.queue = queueFilter
      if (statusFilter) params.status = statusFilter
      if (contentTypeFilter) params.content_type = contentTypeFilter
      if (assignedFilter === 'me') params.assigned = 'me'
      if (assignedFilter === 'unassigned') params.assigned = 'unassigned'
      if (search.trim()) params.q = search.trim()
      const { items, next } = await listModqueueEvents(params)
      setEvents(items)
      setNextUrl(next || null)
      setSelectedIds((prev) => {
        const visible = new Set(items.map((item) => item.event_id))
        const nextIds = new Set<string>()
        prev.forEach((id) => {
          if (visible.has(id)) nextIds.add(id)
        })
        return nextIds
      })
      setSelectedId((prev) => {
        if (prev) return prev
        return items[0]?.event_id ?? null
      })
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load moderation queue.')
    } finally {
      setListLoading(false)
    }
  }

  const loadMore = async () => {
    if (!nextUrl) return
    setListLoading(true)
    setError(null)
    try {
      const { items, next } = await listModqueueEvents(undefined, nextUrl)
      setEvents((prev) => [...prev, ...items])
      setNextUrl(next || null)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load more events.')
    } finally {
      setListLoading(false)
    }
  }

  useEffect(() => {
    loadEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilter, statusFilter, contentTypeFilter, assignedFilter])

  useEffect(() => {
    pollingPausedRef.current = searchFocused || resolveLoading || bulkLoading
  }, [searchFocused, resolveLoading, bulkLoading])

  useEffect(() => {
    const interval = setInterval(() => {
      if (!pollingPausedRef.current) {
        loadEvents()
      }
    }, 10000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFilter, statusFilter, contentTypeFilter, assignedFilter, search])

  useEffect(() => {
    if (!selectedId) {
      setSelected(null)
      return
    }
    setDetailLoading(true)
    setDetailError(null)
    getModqueueEvent(selectedId)
      .then((data) => {
        setSelected(data)
      })
      .catch((err: any) => {
        setDetailError(err?.response?.data?.detail || err?.message || 'Failed to load event details.')
      })
      .finally(() => {
        setDetailLoading(false)
      })
  }, [selectedId])

  useEffect(() => {
    if (!selectedId) {
      setRelatedItems([])
      return
    }
    setRelatedLoading(true)
    setRelatedError(null)
    const load = async () => {
      try {
        if (relatedTab === 'actor') {
          const data = await getRelatedActor(selectedId)
          setRelatedItems(data.items || [])
        } else if (relatedTab === 'domains') {
          const data = await getRelatedDomains(selectedId)
          setRelatedItems(data.items || [])
        } else if (relatedTab === 'revisions') {
          const data = await getRelatedRevisions(selectedId)
          setRelatedItems(data.items || [])
        } else if (relatedTab === 'simhash') {
          const data = await getRelatedSimhash(selectedId)
          setRelatedItems(data.items || [])
        }
      } catch (err: any) {
        setRelatedError(err?.response?.data?.detail || err?.message || 'Failed to load related events.')
      } finally {
        setRelatedLoading(false)
      }
    }
    load()
  }, [selectedId, relatedTab])

  const handleClaim = async (event: ModQueueEvent) => {
    if (!meId) return
    const optimistic = { id: meId, username: me?.profile?.display_name || me?.username }
    setEvents((prev) =>
      prev.map((item) => (item.event_id === event.event_id ? { ...item, assigned_to: optimistic } : item))
    )
    if (selected && selected.event_id === event.event_id) {
      setSelected({ ...selected, assigned_to: optimistic })
    }
    try {
      await claimModqueueEvent(event.event_id)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to claim event.')
      await loadEvents()
    }
  }

  const handleRelease = async (event: ModQueueEvent) => {
    setEvents((prev) =>
      prev.map((item) => (item.event_id === event.event_id ? { ...item, assigned_to: null } : item))
    )
    if (selected && selected.event_id === event.event_id) {
      setSelected({ ...selected, assigned_to: null })
    }
    try {
      await releaseModqueueEvent(event.event_id)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to release event.')
      await loadEvents()
    }
  }

  const handleResolve = async () => {
    if (!selected) return
    try {
      setResolveLoading(true)
      await resolveModqueueEvent(selected.event_id, resolveForm)
      await loadEvents()
      const updated = await getModqueueEvent(selected.event_id)
      setSelected(updated)
    } catch (err: any) {
      setDetailError(err?.response?.data?.detail || err?.message || 'Failed to resolve event.')
    } finally {
      setResolveLoading(false)
    }
  }

  const toggleSelect = (eventId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) next.delete(eventId)
      else next.add(eventId)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const handleBulkResolve = async () => {
    if (selectedIds.size === 0) return
    setBulkLoading(true)
    setBulkError(null)
    try {
      const payload: ModQueueBulkResolvePayload = {
        ...bulkForm,
        event_ids: Array.from(selectedIds),
      }
      const data = await bulkResolveEvents(payload)
      await loadEvents()
      const failed = new Set<string>((data.failed || []).map((item: any) => item.id))
      setSelectedIds(failed)
      if (failed.size > 0) {
        setBulkError(`Resolved ${data.resolved}. ${failed.size} failed due to assignment conflicts.`)
      }
    } catch (err: any) {
      setBulkError(err?.response?.data?.detail || err?.message || 'Bulk resolve failed.')
    } finally {
      setBulkLoading(false)
    }
  }

  const handleResolveAllFromActor = async () => {
    if (!selected) return
    try {
      const data = await getRelatedActor(selected.event_id)
      const ids = new Set<string>([selected.event_id])
      data.items?.forEach((item) => ids.add(item.event_id))
      setSelectedIds(ids)
    } catch (err: any) {
      setDetailError(err?.response?.data?.detail || err?.message || 'Failed to load actor events.')
    }
  }

  const isAssignedToMe = (event: ModQueueEvent | ModQueueEventDetail | null) => {
    if (!event || !meId) return false
    return event.assigned_to?.id === meId
  }

  const handleAppealReview = async (appealId: string, status: 'accepted' | 'denied') => {
    if (!selected) return
    setAppealError(null)
    setAppealLoading((prev) => ({ ...prev, [appealId]: true }))
    try {
      await reviewAppeal(appealId, {
        status,
        staff_note: appealNotes[appealId] || '',
        user_note: appealUserNotes[appealId] || '',
      })
      await loadEvents()
      const updated = await getModqueueEvent(selected.event_id)
      setSelected(updated)
    } catch (err: any) {
      setAppealError(err?.response?.data?.detail || err?.message || 'Failed to review appeal.')
    } finally {
      setAppealLoading((prev) => ({ ...prev, [appealId]: false }))
    }
  }

  const profileContext =
    selected && selected.content_type === 'profile' && selected.context && typeof selected.context === 'object'
      ? selected.context
      : null
  const listingContext =
    selected && selected.content_type === 'listing' && selected.context && typeof selected.context === 'object'
      ? selected.context
      : null
  const profilePublicId = profileContext?.public_id
  const profileHandle = profileContext?.handle
  const listingPublicId = listingContext?.public_id
  const contentHref =
    selected?.content_type === 'post' && selected?.content_id
      ? `/p/${encodeURIComponent(selected.content_id)}`
      : selected?.content_type === 'profile' && profilePublicId
        ? `/people/i/${encodeURIComponent(profilePublicId)}`
        : selected?.content_type === 'listing' && listingPublicId
          ? `/store/i/${encodeURIComponent(listingPublicId)}`
          : null
  const trustScore =
    selected?.signals?.trust?.score ?? selected?.trust_snapshot?.score ?? null
  const trustTier =
    selected?.signals?.trust?.tier ?? selected?.trust_snapshot?.tier ?? null
  const trustFactors =
    selected?.signals?.trust?.factors ?? selected?.trust_snapshot?.factors ?? null
  const trustSummary = useMemo(() => summarizeTrustFactors(trustFactors), [trustFactors])
  const selectedDomains = useMemo(() => {
    if (!selected) return []
    if (selected.link_domains?.length) return selected.link_domains
    const domains = new Set<string>()
    selected.signals?.links?.forEach((link) => {
      if (link.domain) domains.add(link.domain)
    })
    return Array.from(domains)
  }, [selected])

  const outerClassName = containerClassName || 'mx-auto max-w-7xl px-4 py-8'
  const pageClass = pageClassName || 'min-h-full bg-black text-white'

  return (
    <div className={pageClass}>
      <style>{`
        .modqueue-select option {
          background-color: #000;
          color: #fff;
        }
      `}</style>
      <div className={outerClassName}>
        {showPageTitle && (
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Moderation Queue</h1>
            <p className="text-sm text-white/70">Review escalated moderation events and resolve actions.</p>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-5">
          <select
            className="modqueue-select glass rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white"
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            style={{ colorScheme: 'dark' }}
          >
            {QUEUE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="modqueue-select glass rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ colorScheme: 'dark' }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="modqueue-select glass rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white"
            value={contentTypeFilter}
            onChange={(e) => setContentTypeFilter(e.target.value)}
            style={{ colorScheme: 'dark' }}
          >
            {CONTENT_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="modqueue-select glass rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white"
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value as any)}
            style={{ colorScheme: 'dark' }}
          >
            <option value="all">All assignments</option>
            <option value="me">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <div className="flex gap-2">
            <input
              className="glass w-full rounded-xl border border-white/10 bg-transparent px-3 py-2 text-sm"
              placeholder="Search content ID or actor"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadEvents()
              }}
            />
            <button
              className="rounded-xl border border-white/15 px-3 py-2 text-xs font-semibold hover:bg-white/10"
              onClick={loadEvents}
            >
              Search
            </button>
          </div>
        </div>

        {error && <div className="mb-4 text-sm text-red-400">{error}</div>}
        {bulkError && <div className="mb-4 text-sm text-red-400">{bulkError}</div>}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px,1fr]">
          <div className="glass rounded-2xl border border-white/10 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-white/30 bg-black"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                />
                <span>Queue</span>
              </div>
              <button
                className="text-xs text-white/60 hover:text-white"
                onClick={loadEvents}
                disabled={listLoading}
              >
                {listLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
              <div className="max-h-[640px] space-y-2 overflow-y-auto">
                {events.map((event) => {
                  const assignedLabel = event.assigned_to
                    ? event.assigned_to.id === meId
                      ? 'me'
                      : event.assigned_to.username || event.assigned_to.email || 'assigned'
                    : 'unassigned'
                  const hasReports = (event.reports_count || 0) > 0
                  return (
                    <button
                      key={event.event_id}
                      onClick={() => setSelectedId(event.event_id)}
                      className={`w-full rounded-xl border px-3 py-2 text-left transition ${
                        selectedId === event.event_id
                          ? 'border-brand bg-brand/15'
                          : hasReports
                            ? 'border-amber-400/30 bg-amber-500/5 hover:bg-amber-500/10'
                            : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between text-xs text-white/70">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                          className="h-3.5 w-3.5 rounded border-white/30 bg-black"
                          checked={selectedIds.has(event.event_id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleSelect(event.event_id)}
                          />
                          <span>{event.content_type}</span>
                          {hasReports && (
                            <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-100">
                              {event.reports_count} report{event.reports_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <span>{formatDate(event.created_at)}</span>
                      </div>
                      <div className="mt-1 text-sm font-semibold">{event.content_id || event.event_id}</div>
                      <div className="mt-1 text-xs text-white/70">{event.text_excerpt}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-white/60">
                      <span>actor: {formatUserLabel(event.actor)}</span>
                      <span>assigned: {assignedLabel}</span>
                      {event.current_decision && (
                        <span>rec: {mapActionKeyToLabel(event.current_decision.action)} ({event.current_decision.confidence})</span>
                      )}
                      {event.signal_summary && (
                        <>
                          <span>rules: {event.signal_summary.rules_count}</span>
                          {event.signal_summary.domains?.length > 0 && (
                            <span>domains: {event.signal_summary.domains.join(', ')}</span>
                          )}
                          {event.signal_summary.trust_score !== undefined &&
                            event.signal_summary.trust_score !== null && (
                              <span>
                                trust: {event.signal_summary.trust_score}
                                {event.signal_summary.trust_tier ? ` (${event.signal_summary.trust_tier})` : ''}
                              </span>
                              )}
                          </>
                        )}
                        {hasReports && event.latest_reported_at && (
                          <span>last report: {formatRelative(event.latest_reported_at)}</span>
                        )}
                        <span>appeals: {event.appeals_count}</span>
                      </div>
                    </button>
                  )
                })}
              {events.length === 0 && !listLoading && (
                <div className="rounded-xl border border-dashed border-white/10 p-4 text-sm text-white/60">
                  No events match these filters.
                </div>
              )}
            </div>
            {nextUrl && (
              <button
                className="mt-3 w-full rounded-xl border border-white/15 px-3 py-2 text-sm font-semibold hover:bg-white/10"
                onClick={loadMore}
                disabled={listLoading}
              >
                {listLoading ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>

          <div className="glass rounded-2xl border border-white/10 p-4">
            {!selectedId && <div className="text-sm text-white/60">Select an event to review.</div>}
            {detailLoading && <div className="text-sm text-white/60">Loading event details...</div>}
            {detailError && <div className="text-sm text-red-400">{detailError}</div>}
            {selected && !detailLoading && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm text-white/70">Content</div>
                    <div className="text-lg font-semibold">
                      {selected.content_type} - {selected.content_id}
                    </div>
                    {contentHref && (
                      <a
                        href={contentHref}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex text-xs text-emerald-300 hover:text-emerald-200"
                      >
                        {selected.content_type === 'profile'
                          ? 'View profile'
                          : selected.content_type === 'listing'
                            ? 'View listing'
                            : 'View post'}
                      </a>
                    )}
                  </div>
                  <div className="text-sm text-white/70">
                    Status: <span className="text-white">{selected.status}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Actor</div>
                    <div className="font-semibold">{formatUserLabel(selected.actor)}</div>
                    <div className="mt-2 text-xs text-white/60">Queue</div>
                    <div>{selected.queue}</div>
                    <div className="mt-2 text-xs text-white/60">Assigned</div>
                    <div>{selected.assigned_to ? formatUserLabel(selected.assigned_to) : 'Unassigned'}</div>
                    {trustScore !== null && trustScore !== undefined && (
                      <>
                        <div className="mt-2 text-xs text-white/60">Trust</div>
                        <div>
                          {trustScore}
                          {trustTier ? ` (${trustTier})` : ''}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Created</div>
                    <div>{formatDate(selected.created_at)}</div>
                    <div className="mt-2 text-xs text-white/60">Claims</div>
                    <div>{selected.claimed_at ? formatDate(selected.claimed_at) : 'N/A'}</div>
                    <div className="mt-2 text-xs text-white/60">Resolved</div>
                    <div>{selected.resolved_at ? formatDate(selected.resolved_at) : 'N/A'}</div>
                  </div>
                </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs text-white/60">Text</div>
                    <div className="whitespace-pre-wrap text-sm">{selected.text || 'N/A'}</div>
                  </div>

                  {selected.content_type === 'profile' && profileContext && (
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                      <div className="text-xs text-white/60">Profile</div>
                      <div className="mt-1 font-semibold">
                        {profileContext.display_name || 'Unnamed'}{' '}
                        {profileHandle ? <span className="text-white/60">@{profileHandle}</span> : null}
                      </div>
                      {profileContext.bio && (
                        <div className="mt-2 text-xs text-white/70 whitespace-pre-wrap">{profileContext.bio}</div>
                      )}
                      {Array.isArray(profileContext.profile_links) && profileContext.profile_links.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {profileContext.profile_links.slice(0, 4).map((url: string) => (
                            <span key={url} className="rounded-full border border-white/10 px-2 py-0.5 text-[11px]">
                              {url}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selected.content_type === 'listing' && listingContext && (
                    <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                      <div className="text-xs text-white/60">Listing</div>
                      <div className="mt-1 font-semibold">
                        {listingContext.title || 'Untitled'}{' '}
                        {listingContext.currency && listingContext.price_pixe ? (
                          <span className="text-white/60">
                            {listingContext.price_pixe} {listingContext.currency}
                          </span>
                        ) : null}
                      </div>
                      {listingContext.subtitle && (
                        <div className="mt-1 text-xs text-white/70">{listingContext.subtitle}</div>
                      )}
                      {listingContext.description && (
                        <div className="mt-2 text-xs text-white/70 whitespace-pre-wrap">
                          {listingContext.description}
                        </div>
                      )}
                      {Array.isArray(selected.link_domains) && selected.link_domains.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selected.link_domains.slice(0, 4).map((domain) => (
                            <span key={domain} className="rounded-full border border-white/10 px-2 py-0.5 text-[11px]">
                              {domain}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Links</div>
                    <div>{selected.links?.length || 0}</div>
                    {selected.links?.slice(0, 5).map((link, idx) => (
                      <div key={idx} className="text-xs text-white/70">
                        {link?.url || JSON.stringify(link)}
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Media</div>
                    <div>{selected.media?.length || 0}</div>
                  </div>
                </div>

                {selected.current_decision && (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Current Decision</div>
                    <div className="font-semibold">{mapActionKeyToLabel(selected.current_decision.action)}</div>
                    <div className="text-xs text-white/70">
                      Categories: {selected.current_decision.categories?.join(', ') || 'N/A'}
                    </div>
                    <div className="text-xs text-white/70">
                      Severity: {selected.current_decision.severity} / Confidence: {selected.current_decision.confidence}
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/60">Reports</div>
                    <div className="text-xs text-white/60">
                      {selected.reports_count || 0} total
                      {selected.latest_reported_at ? ` - ${formatDate(selected.latest_reported_at)}` : ''}
                    </div>
                  </div>
                  {selected.reports && selected.reports.length > 0 ? (
                    <div className="mt-2 space-y-2 text-xs text-white/70">
                      {selected.reports.slice(0, 6).map((report) => (
                        <div key={report.id} className="rounded-lg border border-white/10 bg-black/30 p-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{report.reason}</span>
                            <span className="text-white/60">{formatDate(report.created_at)}</span>
                          </div>
                          {report.note && <div className="mt-1 text-white/70">{report.note}</div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-white/60">No reports yet.</div>
                  )}
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/60">Appeals</div>
                    <div className="text-xs text-white/60">{selected.appeals_count || 0} total</div>
                  </div>
                  {appealError && <div className="mt-2 text-xs text-red-400">{appealError}</div>}
                  {selected.appeals && selected.appeals.length > 0 ? (
                    <div className="mt-2 space-y-2 text-xs text-white/70">
                      {selected.appeals.slice(0, 6).map((appeal) => {
                        const loading = Boolean(appealLoading[appeal.id])
                        const noteValue = appealNotes[appeal.id] || ''
                        const userNoteValue = appealUserNotes[appeal.id] || ''
                        return (
                          <div key={appeal.id} className="rounded-lg border border-white/10 bg-black/30 p-2">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">{appeal.status}</span>
                              <span className="text-white/60">{formatDate(appeal.created_at)}</span>
                            </div>
                            <div className="mt-1 text-white/80 whitespace-pre-wrap">{appeal.message}</div>
                            {appeal.reviewed_at && (
                              <div className="mt-1 text-white/60">
                                Reviewed {formatDate(appeal.reviewed_at)}
                                {appeal.reviewed_by?.username ? ` by ${appeal.reviewed_by.username}` : ''}
                              </div>
                            )}
                            {appeal.staff_note && <div className="mt-1 text-white/60">Staff note: {appeal.staff_note}</div>}
                            {appeal.user_note && <div className="mt-1 text-white/70">User note: {appeal.user_note}</div>}
                            {appeal.status === 'open' && (
                              <div className="mt-2 space-y-2">
                                <textarea
                                  rows={2}
                                  value={noteValue}
                                  onChange={(e) => setAppealNotes((prev) => ({ ...prev, [appeal.id]: e.target.value }))}
                                  placeholder="Internal note (optional)"
                                  className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-xs text-white"
                                />
                                <textarea
                                  rows={2}
                                  value={userNoteValue}
                                  onChange={(e) => setAppealUserNotes((prev) => ({ ...prev, [appeal.id]: e.target.value }))}
                                  placeholder="User-safe note (optional)"
                                  className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-xs text-white"
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    className="rounded-lg bg-emerald-500/80 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                                    onClick={() => handleAppealReview(appeal.id, 'accepted')}
                                    disabled={loading}
                                  >
                                    Accept appeal
                                  </button>
                                  <button
                                    className="rounded-lg border border-white/20 px-3 py-1 text-xs font-semibold text-white/80 hover:bg-white/10 disabled:opacity-60"
                                    onClick={() => handleAppealReview(appeal.id, 'denied')}
                                    disabled={loading}
                                  >
                                    Deny appeal
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-white/60">No appeals yet.</div>
                  )}
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/60">Audit</div>
                    <div className="text-xs text-white/60">{selected.audit?.length || 0} recent</div>
                  </div>
                  {selected.audit && selected.audit.length > 0 ? (
                    <div className="mt-2 space-y-2 text-xs text-white/70">
                      {selected.audit.map((entry) => (
                        <div key={entry.id} className="rounded-lg border border-white/10 bg-black/30 p-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{entry.action_type}</span>
                            <span className="text-white/60">{formatDate(entry.created_at)}</span>
                          </div>
                          {entry.summary && <div className="mt-1 text-white/70">{entry.summary}</div>}
                          {entry.actor?.username && (
                            <div className="mt-1 text-white/60">by {entry.actor.username}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-white/60">No audit activity yet.</div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="text-xs text-white/60">Signals</div>
                    <div className="mt-2 text-xs text-white/70">
                      Trust: {trustScore ?? 'N/A'}
                      {trustTier ? ` (${trustTier})` : ''}
                    </div>
                    {selectedDomains.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedDomains.map((domain) => (
                          <span key={domain} className="rounded-full border border-white/10 px-2 py-0.5 text-[11px]">
                            {domain}
                          </span>
                        ))}
                      </div>
                    )}
                    {(trustSummary.negative.length > 0 || trustSummary.positive.length > 0) && (
                      <div className="mt-3 text-[11px] text-white/70">
                        {trustSummary.negative.length > 0 && (
                          <div>
                            Negatives:{' '}
                            {trustSummary.negative
                              .map((item) => `${item.key} ${item.delta}`)
                              .join(', ')}
                          </div>
                        )}
                        {trustSummary.positive.length > 0 && (
                          <div>
                            Positives:{' '}
                            {trustSummary.positive
                              .map((item) => `${item.key} +${item.delta}`)
                              .join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-3 space-y-2 text-xs text-white/70">
                      {(selected.signals?.rules || []).slice(0, 6).map((rule, idx) => (
                        <div key={`${rule.id}-${idx}`}>
                          <span className="font-semibold">{rule.id}</span>
                          {rule.matches?.length ? `: ${rule.matches.join(', ')}` : ''}
                        </div>
                      ))}
                      {(selected.signals?.notes || []).length > 0 && (
                        <div>Notes: {selected.signals?.notes?.join(', ')}</div>
                      )}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-xs text-white/60">Related</div>
                      <div className="flex gap-2 text-[11px]">
                        {['actor', 'domains', 'revisions', 'simhash'].map((tab) => (
                          <button
                            key={tab}
                            className={`rounded-full border px-2 py-0.5 ${
                              relatedTab === tab ? 'border-brand bg-brand/30' : 'border-white/10 bg-white/5'
                            }`}
                            onClick={() => setRelatedTab(tab as any)}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>
                    </div>
                    {relatedLoading && <div className="text-xs text-white/60">Loading...</div>}
                    {relatedError && <div className="text-xs text-red-400">{relatedError}</div>}
                    {!relatedLoading && relatedItems.length === 0 && (
                      <div className="text-xs text-white/60">No related events.</div>
                    )}
                    <div className="mt-2 space-y-2">
                      {relatedItems.map((item) => (
                        <button
                          key={item.event_id}
                          onClick={() => setSelectedId(item.event_id)}
                          className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-left text-xs hover:bg-white/10"
                        >
                          <div className="flex items-center justify-between text-white/70">
                            <span>{item.content_type}</span>
                            <span>rev {item.revision}</span>
                          </div>
                          <div className="font-semibold">{item.content_id || item.event_id}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!selected.assigned_to && (
                    <button
                      className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold"
                      onClick={() => handleClaim(selected)}
                      disabled={!meId}
                    >
                      Claim
                    </button>
                  )}
                  {selected.assigned_to && isAssignedToMe(selected) && (
                    <button
                      className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/10"
                      onClick={() => handleRelease(selected)}
                    >
                      Release
                    </button>
                  )}
                  {selected.actor && (
                    <button
                      className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold hover:bg-white/10"
                      onClick={handleResolveAllFromActor}
                    >
                      Resolve all from actor
                    </button>
                  )}
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
                  <div className="mb-3 text-sm font-semibold">Resolve</div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-xs text-white/60">
                      Final action
                      <select
                        className="modqueue-select mt-1 w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm text-white"
                        value={resolveForm.final_action}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, final_action: e.target.value }))}
                        style={{ colorScheme: 'dark' }}
                      >
                        {ACTION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-white/60">
                      Resolution
                      <select
                        className="modqueue-select mt-1 w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm text-white"
                        value={resolveForm.resolution}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, resolution: e.target.value as any }))}
                        style={{ colorScheme: 'dark' }}
                      >
                        {RESOLUTION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-white/60">
                      Severity ({resolveForm.severity})
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={resolveForm.severity}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, severity: Number(e.target.value) }))}
                        className="mt-2 w-full"
                      />
                    </label>
                    <label className="text-xs text-white/60">
                      Confidence
                      <input
                        type="number"
                        min={0}
                        max={1}
                        step={0.05}
                        value={resolveForm.confidence}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, confidence: Number(e.target.value) }))}
                        className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                      />
                    </label>
                  </div>

                  <div className="mt-3 text-xs text-white/60">Categories</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {CATEGORY_OPTIONS.map((cat) => {
                      const active = resolveForm.categories?.includes(cat)
                      return (
                        <button
                          key={cat}
                          type="button"
                          className={`rounded-full border px-3 py-1 text-xs ${
                            active ? 'border-brand bg-brand/30' : 'border-white/10 bg-white/5'
                          }`}
                          onClick={() => {
                            setResolveForm((prev) => {
                              const current = new Set(prev.categories || [])
                              if (current.has(cat)) current.delete(cat)
                              else current.add(cat)
                              return { ...prev, categories: Array.from(current) }
                            })
                          }}
                        >
                          {cat}
                        </button>
                      )
                    })}
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <label className="text-xs text-white/60">
                      Rationale (short)
                      <input
                        className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                        value={resolveForm.rationale_short || ''}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, rationale_short: e.target.value }))}
                      />
                    </label>
                    <label className="text-xs text-white/60">
                      Resolution note
                      <input
                        className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                        value={resolveForm.resolution_note || ''}
                        onChange={(e) => setResolveForm((prev) => ({ ...prev, resolution_note: e.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="mt-3 block text-xs text-white/60">
                    Internal notes
                    <textarea
                      rows={3}
                      className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                      value={resolveForm.rationale_internal || ''}
                      onChange={(e) => setResolveForm((prev) => ({ ...prev, rationale_internal: e.target.value }))}
                    />
                  </label>

                  <button
                    className="mt-4 w-full rounded-xl bg-brand px-4 py-2 text-sm font-semibold disabled:opacity-60"
                    onClick={handleResolve}
                    disabled={!isAssignedToMe(selected) || resolveLoading}
                  >
                    {resolveLoading ? 'Resolving...' : 'Resolve event'}
                  </button>
                  {!isAssignedToMe(selected) && (
                    <div className="mt-2 text-xs text-white/60">
                      Claim the event to resolve it.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {hasSelection && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-black/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold">Bulk resolve ({selectedIds.size})</div>
              <button
                className="text-xs text-white/60 hover:text-white"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <label className="text-xs text-white/60">
                Final action
                <select
                  className="modqueue-select mt-1 w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm text-white"
                  value={bulkForm.final_action}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, final_action: e.target.value }))}
                  style={{ colorScheme: 'dark' }}
                >
                  {ACTION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-white/60">
                Resolution
                <select
                  className="modqueue-select mt-1 w-full rounded-lg border border-white/10 bg-black px-2 py-1 text-sm text-white"
                  value={bulkForm.resolution}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, resolution: e.target.value as any }))}
                  style={{ colorScheme: 'dark' }}
                >
                  {RESOLUTION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-white/60">
                Confidence
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={bulkForm.confidence}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, confidence: Number(e.target.value) }))}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                />
              </label>
            </div>

            <label className="mt-3 block text-xs text-white/60">
              Severity ({bulkForm.severity})
              <input
                type="range"
                min={0}
                max={100}
                value={bulkForm.severity}
                onChange={(e) => setBulkForm((prev) => ({ ...prev, severity: Number(e.target.value) }))}
                className="mt-2 w-full"
              />
            </label>

            <div className="mt-3 text-xs text-white/60">Categories</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((cat) => {
                const active = bulkForm.categories?.includes(cat)
                return (
                  <button
                    key={cat}
                    type="button"
                    className={`rounded-full border px-3 py-1 text-xs ${
                      active ? 'border-brand bg-brand/30' : 'border-white/10 bg-white/5'
                    }`}
                    onClick={() => {
                      setBulkForm((prev) => {
                        const current = new Set(prev.categories || [])
                        if (current.has(cat)) current.delete(cat)
                        else current.add(cat)
                        return { ...prev, categories: Array.from(current) }
                      })
                    }}
                  >
                    {cat}
                  </button>
                )
              })}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="text-xs text-white/60">
                Rationale (short)
                <input
                  className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                  value={bulkForm.rationale_short || ''}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, rationale_short: e.target.value }))}
                />
              </label>
              <label className="text-xs text-white/60">
                Internal notes
                <input
                  className="mt-1 w-full rounded-lg border border-white/10 bg-transparent px-2 py-1 text-sm"
                  value={bulkForm.rationale_internal || ''}
                  onChange={(e) => setBulkForm((prev) => ({ ...prev, rationale_internal: e.target.value }))}
                />
              </label>
            </div>

            <button
              className="mt-4 w-full rounded-xl bg-brand px-4 py-2 text-sm font-semibold disabled:opacity-60"
              onClick={handleBulkResolve}
              disabled={bulkLoading}
            >
              {bulkLoading ? 'Resolving...' : `Resolve ${selectedIds.size}`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
